import React, {type ChangeEvent, type MouseEvent, type ReactNode, useEffect, useId, useRef, useState} from 'react'
import _ from 'lodash'
import type KeyValuePair from '@/Library/Classes/KeyValuePair'
import greenCheck from './../../../images/check-circle.svg'
import {Transition, TransitionChild} from '@headlessui/react'
import PopoverDrawerHeader from '@/Components/Modal/PopoverDrawerHeader'
import InputErrorLabel from '@/Components/Forms/InputErrorLabel'
import {Input} from '@/Types/Input'

interface SelectDropdownProps extends Input {
    id?: string
    testId?: string
    type: 'custom' | 'native'
    options: KeyValuePair[]
    title: string
}

export default function SelectDropdown(props: SelectDropdownProps): ReactNode {
    const [currentValue, setCurrentValue] = useState<string>('')
    const [overlayVisible, setOverlayVisible] = useState<boolean>(false)
    const nativeRef = useRef<HTMLSelectElement>(null)
    const descriptionId = useId()

    useEffect(() => {
        const initialValue: string = !Array.isArray(props.value) ? props.value ?? '' : ''

        setCurrentValue(initialValue)

        if (initialValue.length > 0) {
            if ((nativeRef?.current) != null) {
                nativeRef.current.value = initialValue
            }
        }
    }, [])

    const labelClasses = (): string => {
        let base: string = 'form-control-label'

        if (props.error != null) {
            base += ' has-error'
        }

        return base
    }

    const inputClasses = (): string => {
        let base = 'form-control-select'

        if (props.error != null) {
            base += ' has-error'
        }

        return base
    }

    const formattedTitle = (): string | null => {
        if (props.title != null) {
            return _.camelCase(props.title)
        }

        return null
    }

    const handleNativeMouseDown = (event: MouseEvent<HTMLSelectElement>): void => {
        if (props.type === 'custom') {
            event.preventDefault()
            setOverlayVisible(true)
            document.querySelector('html')?.classList.add('overflow-hidden', 'h-screen')
        }
    }

    const handleNativeChange = (event: ChangeEvent<HTMLSelectElement>): void => {
        setValue(event.target.value)
    }

    const setValue = (value: string): void => {
        setCurrentValue(value)

        if (props.setData != null) {
            props.setData(props.field ?? formattedTitle() ?? '', value)
        }
    }

    const handleCustomChange = (value: string): void => {
        setValue(value)

        if ((nativeRef?.current) != null) {
            nativeRef.current.value = value
        }

        setOverlayVisible(false)
        resetPageOverflow()
    }

    const resetPageOverflow = (): void => {
        document.querySelector('html')?.classList.remove('overflow-hidden', 'h-screen')
    }

    const close = (): void => {
        setOverlayVisible(false)
        resetPageOverflow()
    }

    const PopoverElement = (): ReactNode | null => {
        if (props.type === 'custom') {
            return (
                <Transition
                    appear={true}
                    show={overlayVisible}
                    enter="transition-opacity duration-200"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="transition-opacity duration-150"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                    as={'div'}
                    className={'fixed w-screen h-screen left-0 top-0 z-50 bg-storm-20'}
                >
                    <div className={'absolute w-full h-full'} onClick={close}></div>
                    <TransitionChild
                        appear={true}
                        enter="transition ease-in-out duration-300 transform"
                        enterFrom="translate-y-full"
                        enterTo="translate-y-0"
                        leave="transition ease-in-out duration-300 transform"
                        leaveFrom="translate-y-0"
                        leaveTo="translate-y-full"
                        as={'div'}
                        className={'absolute bottom-0 left-0 w-full pb-7 bg-white flex flex-col items-center select-overlay-container max-h-[70vh] overflow-y-scroll transition duration-100 z-20 rounded-t-lg overflow-hidden'}
                    >
                        <PopoverDrawerHeader onClose={close}>
                            {props.title}
                        </PopoverDrawerHeader>
                        <div className={'max-w-lg w-full flex flex-col'}>
                            {props.options.map(option => (
                                <button
                                    className={'flex text-left py-3 hover:bg-storm-90 text-storm-10 px-6 disabled:opacity-50 disabled:cursor-not-allowed'}
                                    key={option.key}
                                    disabled={option.disabled}
                                    onClick={() => {
                                        handleCustomChange(option.key)
                                    }}
                                >
                                    <span className={'inline-block flex-1 font-bold'}>
                                      {option.value}
                                    </span>
                                    <span className={currentValue === option.key ? 'inline-block' : 'hidden'}>
                                      <img src={greenCheck} alt={'checked'}/>
                                    </span>
                                </button>
                            ))}
                        </div>
                    </TransitionChild>
                </Transition>
            )
        }

        return null
    }

    const errorId = props.testId != null ? `${props.testId}-error` : undefined
    const hasDescription = props.description != null && props.description !== ''
    const describedBy = [hasDescription ? descriptionId : null, props.error != null ? errorId ?? null : null]
        .filter((id): id is string => id != null)
        .join(' ') || undefined

    return (
        <div className={`block relative ${props.className}`}>
            {(props.title != null)
                ?
                <label
                    className={labelClasses()}
                    htmlFor={props.id}>{props.title}</label>
                : null}
            {hasDescription
                ? <p id={descriptionId} className={'mb-2 text-sm text-gray-500'}>{props.description}</p>
                : null}
            <select
                id={props.id}
                data-testid={props.testId}
                onChange={handleNativeChange}
                className={inputClasses()}
                onMouseDown={handleNativeMouseDown}
                required={props.required}
                disabled={props.disabled}
                aria-required={props.required || undefined}
                aria-invalid={props.error != null || undefined}
                aria-describedby={describedBy}
                ref={nativeRef}>
                <option value={''}>Select</option>
                {props.options.map(option => (
                    <option key={option.key} value={option.key} disabled={option.disabled}>{option.value}</option>
                ))}
            </select>
            <PopoverElement/>
            <InputErrorLabel error={props.error} id={errorId} testId={errorId}/>
        </div>
    )
}
